// SidebarLayout.js
import React, { useState, useEffect } from "react";
import axios from 'axios';
import { Outlet, Link } from "react-router-dom";
import {
  FaChartLine, FaBook, FaCog, FaList, FaBookOpen, FaFileAlt, FaChartBar, FaUniversity, FaUserCog
} from "react-icons/fa";
import { MdLibraryBooks, MdMenuBook, MdOutlineMenuBook } from "react-icons/md";
import { IoMdClose } from "react-icons/io";
import { NavLink } from 'react-router-dom';
import Logo from '../../assets/eBrary_Logo.png';
import Tooltip from '@mui/material/Tooltip';
import useSessionTimeout from '../../hooks/useSessionTimeout';

const SidebarLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));
  const [isCustomer, setIsCustomer] = useState(false);
  const hasCollege = user && user.collegeId;
  const isLibrarian = user?.role === "Librarian";
  useSessionTimeout(60); // Set session timeout to 60 minutes

  // ✅ Collapse sidebar by default on small screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


    // ✅ Fetch purchased subjects to set isCustomer
  useEffect(() => {
    const fetchMySubjects = async () => {
      try {
        const { data } = await axios.get(`https://backend.scholarkart.in/api/user/mysubjects?userId=${user?._id}`);
        if (Array.isArray(data) && data.length > 0) {
          setIsCustomer(true);
        }
      } catch (error) {
        console.error("Failed to check purchased subjects", error);
      }
    };
    if (user?._id) fetchMySubjects();
  }, [user?._id]);

  return (
    <div className="bg-gray-100 text-gray-900 flex flex-col md:flex-row h-screen transition-all duration-300">
      {/* Sidebar */}
      <div className={`absolute md:relative h-full p-6 transition-transform duration-300 flex flex-col z-40
        ${sidebarOpen ? "w-72" : "w-16 md:w-20"} bg-gradient-to-b from-blue-900 to-blue-950 text-white shadow-lg`}>
        
        {sidebarOpen && (
          <div className="flex justify-between items-center mb-6 bg-white p-3 rounded-lg shadow-md">
            <Link to="/">
              <img src={Logo} alt="eBrary Guru Logo" className="h-10" />
            </Link>
            <button onClick={() => setSidebarOpen(!sidebarOpen)}>
              <IoMdClose size={24} className="text-blue-500" />
            </button>
          </div>
        )}

        {!sidebarOpen && (
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white mb-6">
            <FaList size={24} />
          </button>
        )}

        <nav className="space-y-4 flex flex-col items-start">
        <NavLink
          to="/mycourse"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="My Course" placement="right" disableHoverListener={sidebarOpen}>
            <div>
              <MdMenuBook />
            </div>
          </Tooltip>
          {sidebarOpen && <span>My Course</span>}
        </NavLink>

        {hasCollege && (
        <NavLink
          to="/mylibrary"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="My Library" placement="right" disableHoverListener={sidebarOpen}>
            <div>
              <MdLibraryBooks />
            </div>
          </Tooltip>
          {sidebarOpen && <span>My Library</span>}
        </NavLink>
        )}

        {(hasCollege || isCustomer) && (
        <NavLink
          to="/ebrarycollection"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="eBrary Collection" placement="right" disableHoverListener={sidebarOpen}>
            <div>
              <FaBookOpen />
            </div>
          </Tooltip>
          {sidebarOpen && <span>eBrary Collection</span>}
        </NavLink>
        )}


        {(hasCollege || isCustomer) && (
        <NavLink
          to="/ejournals"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="Research Journals" placement="right" disableHoverListener={sidebarOpen}>
            <div>
            <FaFileAlt />
            </div>
          </Tooltip>
          {sidebarOpen && <span>Research Journals</span>}
        </NavLink>
        )}

        {(hasCollege || isCustomer) && (
        <NavLink
          to="/journal_articles"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="Research Articles" placement="right" disableHoverListener={sidebarOpen}>
            <div>
            <MdOutlineMenuBook />
            </div>
          </Tooltip>
          {sidebarOpen && <span>Research Articles</span>}
        </NavLink>
        )}

        {isLibrarian && (
        <NavLink
          to="/librarymanagement"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="Library Management" placement="right" disableHoverListener={sidebarOpen}>
            <div>
            <FaUniversity />
            </div>
          </Tooltip>
          {sidebarOpen && <span>Library Management</span>}
        </NavLink>
        )}

      {isLibrarian && (
        <NavLink
          to="/usermanagement"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="User Management" placement="right" disableHoverListener={sidebarOpen}>
            <div>
            <FaUserCog />
            </div>
          </Tooltip>
          {sidebarOpen && <span>User Management</span>}
        </NavLink>
        )}

        {isLibrarian && (
        <NavLink
          to="/usagereports"
          className={({ isActive }) =>
            `flex items-center space-x-3 text-0.5g px-1 py-1 rounded-xl transition-all ${
              isActive ? "bg-blue-800 font-semibold" : "hover:bg-blue-800"
            } text-white`
          }
        >
          <Tooltip title="Usage Reports" placement="right" disableHoverListener={sidebarOpen}>
            <div>
            <FaChartBar />
            </div>
          </Tooltip>
          {sidebarOpen && <span>Usage Reports</span>}
        </NavLink>
      )}

        </nav>
      </div>

      <div
        className={`flex-1 flex flex-col overflow-y-auto bg-gradient-to-br from-gray-50 to-gray-200 transition-all duration-300
          md:pl-0 ${sidebarOpen ? "pl-72" : "pl-16"} p-4`}
      >

        <Outlet />
      </div>
    </div>
  );
};

export default SidebarLayout;
